import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus, Loader2, Calendar, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useCompanies } from "@/hooks/useCompanies";
import { DataPagination } from "@/components/shared/DataPagination";
import type { Campaign } from "@/types/api";

const STATUS_LABELS: Record<string, string> = {
  aguardando_aceite: "Aguardando Aceite",
  ativa: "Ativa",
  encerrada: "Encerrada",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  aguardando_aceite: "secondary",
  ativa: "default",
  encerrada: "destructive",
};

function CampaignCard({ campaign, companyName }: { campaign: Campaign; companyName?: string }) {
  const navigate = useNavigate();
  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={() => navigate(`/campanhas/${campaign.id}`)}
    >
      <CardContent className="p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
            <Megaphone className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate">{campaign.name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <Building2 className="h-3 w-3 shrink-0" />
              <span className="truncate">{companyName ?? "—"}</span>
              <span>·</span>
              <Calendar className="h-3 w-3 shrink-0" />
              <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <Badge variant={STATUS_VARIANT[campaign.status] ?? "outline"} className="shrink-0">
          {STATUS_LABELS[campaign.status] ?? campaign.status}
        </Badge>
      </CardContent>
    </Card>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <Megaphone className="h-10 w-10 text-muted-foreground/40 mb-3" />
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("todas");
  const [page, setPage] = useState(1);
  const { data, isLoading } = useCampaigns(page);
  const { data: companiesData } = useCompanies(1, 100);

  const campaigns = data?.items ?? [];
  const companyMap = Object.fromEntries((companiesData?.items ?? []).map((c) => [c.id, c.name]));

  const filtered = (status?: string) =>
    campaigns.filter((c) => !status || c.status === status);

  const tabs = [
    { value: "todas", label: "Todas", campaigns: filtered() },
    { value: "aguardando_aceite", label: "Aguardando", campaigns: filtered("aguardando_aceite") },
    { value: "ativa", label: "Ativas", campaigns: filtered("ativa") },
    { value: "encerrada", label: "Encerradas", campaigns: filtered("encerrada") },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Campanhas</h1>
        <Button className="gap-2" onClick={() => navigate("/campanhas/nova")}>
          <Plus className="h-4 w-4" />
          Nova Campanha
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
            <TabsList>
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} className="gap-1.5">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value} className="space-y-3 mt-4">
                {t.campaigns.length === 0 ? (
                  <EmptyState label={`Nenhuma campanha ${t.value === "todas" ? "encontrada" : `com status "${STATUS_LABELS[t.value] ?? t.value}"`}.`} />
                ) : (
                  t.campaigns.map((c) => (
                    <CampaignCard key={c.id} campaign={c} companyName={companyMap[c.company_id]} />
                  ))
                )}
              </TabsContent>
            ))}
          </Tabs>
          <DataPagination
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
